// Role-picker config for the "Sign in to your workspace" landing section and
// the per-role split-screen sign-in pages at /login/:slug. Adopted from the
// AllRound Health Concierge design mockup (Aug 2026 redesign, v10).
//
// `role` must match the backend `user.role` value exactly — everything else
// here is presentational only, so getting this wrong does NOT change what
// the account can access, only which sign-in screen it lands on first.
export const ROLE_WORKSPACES = [
  {
    slug: "patient",
    role: "patient",
    label: "Guest",
    org: "Hotel guest access",
    tagline: "In-room care, on your time.",
    description: "Scan the room QR or sign in to message your care team, review prescriptions and join a video visit.",
    authNote: "Single-factor sign-in",
    demoEmail: "guest@aura.demo",
    icon: "User",
    image: "https://images.unsplash.com/photo-1758193783649-13371d7fb8dd?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "doctor",
    role: "doctor",
    label: "Clinician",
    org: "AllRound provider network",
    tagline: "Your queue, your patients, one screen.",
    description: "Review your waiting room, run consultations, and sign prescriptions and lab orders.",
    authNote: "Authenticator code required",
    demoEmail: "dr.chen@aura.demo",
    icon: "Stethoscope",
    image: "https://images.unsplash.com/photo-1673865641073-4479f93a7776?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "hotel-staff",
    role: "hotel_staff",
    label: "Hotel desk",
    org: "Property front office",
    tagline: "Room, guest, doctor, status. Nothing more.",
    description: "Track today's bookings and guest-assistance requests without ever seeing clinical detail.",
    authNote: "Two-factor optional",
    demoEmail: "staff@aura.demo",
    icon: "Building2",
    image: "https://images.unsplash.com/photo-1724230758718-406bab979e67?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "hotel-manager",
    role: "hotel_manager",
    label: "Hotel manager",
    org: "Property management",
    tagline: "Utilization, billing and branding for your property.",
    description: "Everything the front desk sees, plus folio billing, branding and staff management.",
    authNote: "Two-factor optional",
    demoEmail: "manager@aura.demo",
    icon: "Building2",
    image: "https://images.unsplash.com/photo-1637730827702-de34e9ae4ede?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "pharmacy",
    role: "pharmacy",
    label: "Pharmacy partner",
    org: "Pharmacy partner network",
    tagline: "Verify, quote, dispense — in that order.",
    description: "Receive signed prescriptions, verify signatures, quote and dispense against stock.",
    authNote: "Single-factor sign-in",
    demoEmail: "pharmacy1@aura.demo",
    icon: "Pill",
    image: "https://images.unsplash.com/photo-1576602976047-174e57a47881?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "laboratory",
    role: "laboratory",
    label: "Laboratory",
    org: "Laboratory partner network",
    tagline: "From order to result, fully tracked.",
    description: "Quote lab orders, coordinate specimen collection and release results for doctor review.",
    authNote: "Single-factor sign-in",
    demoEmail: "lab1@aura.demo",
    icon: "FlaskConical",
    image: "https://images.pexels.com/photos/13949979/pexels-photo-13949979.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
  },
  {
    slug: "platform-admin",
    role: "platform_admin",
    label: "Platform admin",
    org: "Platform operations",
    tagline: "The control plane for every property and partner.",
    description: "Manage organizations, doctors, hotels, partners, compliance and platform-wide finance.",
    authNote: "Security key / passkey required",
    demoEmail: "platform@aura.demo",
    icon: "ShieldCheck",
    image: "https://images.unsplash.com/photo-1711097383282-28097ae16b1d?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "courier",
    role: "courier",
    label: "Courier",
    org: "Delivery partner network",
    tagline: "Claim it, deliver it, mark it done.",
    description: "Claim pharmacy deliveries near you and track everything currently in progress.",
    authNote: "Single-factor sign-in",
    demoEmail: "courier1@aura.demo",
    icon: "Truck",
    image: "https://images.unsplash.com/photo-1601887031610-d72bf3987fb2?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
  {
    slug: "collector",
    role: "specimen_collector",
    label: "Specimen collector",
    org: "Specimen collection network",
    tagline: "Chain of custody, from claim to check-in.",
    description: "Claim collection tasks and log every custody handoff along the way.",
    authNote: "Single-factor sign-in",
    demoEmail: "collector1@aura.demo",
    icon: "Beaker",
    image: "https://images.pexels.com/photos/8533045/pexels-photo-8533045.jpeg?auto=compress&cs=tinysrgb&h=650&w=940",
  },
];

export const WORKSPACE_BY_SLUG = Object.fromEntries(ROLE_WORKSPACES.map((w) => [w.slug, w]));
export const DEFAULT_WORKSPACE_SLUG = "patient";
